import React, { useCallback, useEffect, useState } from 'react';
import { api, ApiError, clipImageUrl } from './api';
import { shortDate } from './dates';
import { IconDownload, IconShare, IconTrash } from './Icons';
import { navigate } from './router';
import { ShareDialog } from './ShareDialog';
import { downloadBlob } from './share';
import { useToast } from './Toast';
import { ClipInfo } from './types';

export function Clips() {
  const toast = useToast();
  const [list, setList] = useState<ClipInfo[] | null>(null);
  const [share, setShare] = useState<ClipInfo | null>(null);
  const [shareBlob, setShareBlob] = useState<Blob | null>(null);

  // fetch the picture first so it can be shared itself, not only as a link
  const openShare = async (c: ClipInfo) => {
    try {
      const r = await fetch(clipImageUrl(c.id));
      setShareBlob(r.ok ? await r.blob() : null);
    } catch {
      setShareBlob(null);
    }
    setShare(c);
  };

  const load = useCallback(() => {
    api
      .clips()
      .then(setList)
      .catch((e) => {
        setList([]);
        toast(e instanceof ApiError ? e.message : 'Could not load clippings.', 'error');
      });
  }, [toast]);
  useEffect(load, [load]);

  const download = async (c: ClipInfo) => {
    try {
      const r = await fetch(clipImageUrl(c.id));
      downloadBlob(await r.blob(), `clipping-${c.date}-p${c.page}.jpg`);
    } catch {
      toast('Download failed.', 'error');
    }
  };

  const remove = async (c: ClipInfo) => {
    if (!window.confirm('Delete this clipping? Links you shared for it will stop working.')) return;
    try {
      await api.deleteClip(c.id);
      load();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not delete.', 'error');
    }
  };

  const removeAll = async () => {
    if (!list || !list.length) return;
    if (!window.confirm(`Delete all ${list.length} clippings? Links you shared for them will stop working.`)) return;
    try {
      for (const c of list) await api.deleteClip(c.id);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not delete.', 'error');
    }
    load();
  };

  return (
    <div className="page-pad clips-page">
      <div className="clips-head">
        <h2 className="page-title">My clippings</h2>
        {!!list?.length && (
          <button className="link danger-link" onClick={removeAll}>
            Delete all
          </button>
        )}
      </div>
      <p className="hint">Clippings are deleted automatically at the end of every day. Download or share the ones you want to keep.</p>
      {list === null && <p className="muted">Loading…</p>}
      {list && list.length === 0 && <p className="muted">Nothing here yet. Open a page, press <strong>Crop</strong> and cut out a story – it is saved automatically.</p>}
      <div className="clip-grid">
        {list?.map((c) => (
          <article key={c.id} className="clip-card">
            <button className="clip-x" onClick={() => remove(c)} aria-label="Delete this clipping" title="Delete this clipping">
              ×
            </button>
            <button className="clip-img" onClick={() => openShare(c)} aria-label="Share clipping">
              <img src={clipImageUrl(c.id)} alt={`Clipping from ${shortDate(c.date)}, page ${c.page}`} loading="lazy" />
            </button>
            <div className="clip-meta">
              <button className="link" onClick={() => navigate(`/p/${c.date}/${c.page}`)}>
                {shortDate(c.date)} · page {c.page}
              </button>
              <span className="view-chip" title="People who opened the link you shared for this clipping">
                <span aria-hidden="true">👁</span> {c.viewers ?? 0} · {c.views ?? 0}
              </span>
              <div className="row">
                <button className="icon-btn" onClick={() => openShare(c)} aria-label="Share" title="Share">
                  <IconShare size={18} />
                </button>
                <button className="icon-btn" onClick={() => download(c)} aria-label="Download" title="Download">
                  <IconDownload size={18} />
                </button>
                <button className="icon-btn danger" onClick={() => remove(c)} aria-label="Delete" title="Delete">
                  <IconTrash size={18} />
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
      {share && <ShareDialog title="Share clipping" text="" url={share.url} blob={shareBlob} views={{ kind: 'clip', token: share.token }} onClose={() => setShare(null)} />}
    </div>
  );
}
